'use client';

import React, { useState, useEffect } from 'react';
import GalleryFilters from './GalleryFilters';
import GalleryGrid from './GalleryGrid';
import LightboxModal from './LightboxModal';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  title: string;
  description: string;
  category: string;
  isFoodItem: boolean;
  menuLink?: string;
}

const GalleryInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [visibleImages, setVisibleImages] = useState(20);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = ['All', 'Food Items', 'Kitchen', 'Restaurant Ambiance', 'Special Events'];

  // Images from public/assets: Food Items (tonightsp), Kitchen (kitchen), Restaurant Ambiance (Restra)
  const allImages: GalleryImage[] = [
    // Food Items – from /public/assets/tonightsp
    { id: 'f1', src: '/assets/tonightsp/ceasarsalad.png', alt: 'Caesar salad', title: 'Caesar Salad', description: 'Fresh Caesar salad with crisp greens and dressing', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f2', src: '/assets/tonightsp/Clear.png', alt: 'Clear soup', title: 'Clear Soup', description: 'Light and refreshing clear soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f3', src: '/assets/tonightsp/CreameOfChiken.png', alt: 'Cream of chicken soup', title: 'Cream of Chicken', description: 'Creamy chicken soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f4', src: '/assets/tonightsp/CreamOfMashRoom.png', alt: 'Cream of mushroom soup', title: 'Cream of Mushroom', description: 'Rich cream of mushroom soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f5', src: '/assets/tonightsp/Hot n Sour.png', alt: 'Hot and sour soup', title: 'Hot n Sour', description: 'Spicy and tangy hot and sour soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f6', src: '/assets/tonightsp/LemonCriander.png', alt: 'Lemon coriander soup', title: 'Lemon Coriander', description: 'Refreshing lemon and coriander soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f7', src: '/assets/tonightsp/Manchow.png', alt: 'Manchow soup', title: 'Manchow Soup', description: 'Classic Manchow soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f8', src: '/assets/tonightsp/SweetCorn.png', alt: 'Sweet corn soup', title: 'Sweet Corn Soup', description: 'Creamy sweet corn soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f9', src: '/assets/tonightsp/TomKhaSoup.png', alt: 'Tom Kha soup', title: 'Tom Kha Soup', description: 'Thai coconut and galangal soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    { id: 'f10', src: '/assets/tonightsp/TomYum.png', alt: 'Tom Yum soup', title: 'Tom Yum', description: 'Spicy and sour Tom Yum soup', category: 'Food Items', isFoodItem: true, menuLink: '/menu-catalog' },
    // Kitchen – from /public/assets/kitchen
    { id: 'k1', src: '/assets/kitchen/2.png', alt: 'Professional chef in our kitchen', title: 'Our Chef at Work', description: 'Behind the scenes in our state-of-the-art kitchen', category: 'Kitchen', isFoodItem: false },
    { id: 'k2', src: '/assets/kitchen/3.png', alt: 'Fresh ingredients in our kitchen', title: 'Fresh Ingredients', description: 'Quality ingredients in our kitchen', category: 'Kitchen', isFoodItem: false },
    { id: 'k3', src: '/assets/kitchen/kit1.png', alt: 'Modern kitchen facilities', title: 'Kitchen Facilities', description: 'Clean, modern cooking and storage area', category: 'Kitchen', isFoodItem: false },
    // Restaurant Ambiance – from /public/assets/Restra
    { id: 'r1', src: '/assets/Restra/1.jpeg', alt: 'Restaurant interior', title: 'Our Restaurant', description: 'Welcome to our dining space', category: 'Restaurant Ambiance', isFoodItem: false },
    { id: 'r2', src: '/assets/Restra/2.peg.jpeg', alt: 'Restaurant ambiance', title: 'Dining Area', description: 'Comfortable seating and warm ambiance', category: 'Restaurant Ambiance', isFoodItem: false },
    { id: 'r3', src: '/assets/Restra/3.jpeg', alt: 'Restaurant view', title: 'Restaurant View', description: 'Our restaurant atmosphere', category: 'Restaurant Ambiance', isFoodItem: false },
    { id: 'r4', src: '/assets/Restra/4.jpeg', alt: 'Restaurant space', title: 'Dining Space', description: 'Elegant dining at Freshlymade', category: 'Restaurant Ambiance', isFoodItem: false },
    { id: 'r5', src: '/assets/Restra/5.jpeg', alt: 'Restaurant interior', title: 'Ambiance', description: 'Experience our restaurant', category: 'Restaurant Ambiance', isFoodItem: false },
  ];

  const filteredImages =
    activeCategory === 'All'
      ? allImages
      : allImages.filter((img) => img.category === activeCategory);

  const displayedImages = filteredImages.slice(0, visibleImages);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setVisibleImages(20);
  };

  const handleImageClick = (image: GalleryImage) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handleNext = () => {
    if (!selectedImage) return;
    const currentIndex = filteredImages.findIndex((img) => img.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIndex]);
  };

  const handlePrevious = () => {
    if (!selectedImage) return;
    const currentIndex = filteredImages.findIndex((img) => img.id === selectedImage.id);
    const previousIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setSelectedImage(filteredImages[previousIndex]);
  };

  const handleFavorite = (imageId: string) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(imageId)) {
        newFavorites.delete(imageId);
      } else {
        newFavorites.add(imageId);
      }
      return newFavorites;
    });
  };

  const handleShare = (platform: string) => {
    if (!selectedImage) return;
    const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/food-gallery`;
    const shareText = `Check out ${selectedImage.title} at Freshlymade!`;

    const shareUrls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    };

    if (isHydrated && shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleLoadMore = () => {
    setVisibleImages((prev) => prev + 20);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-12 text-center">
          <h1 className="font-heading text-4xl font-bold text-foreground sm:text-5xl">
            Food Gallery
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Explore our culinary creations and restaurant ambiance
          </p>
        </div>
      </div>

      {/* Filters */}
      <GalleryFilters
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
      />

      {/* Gallery Grid */}
      <div className="mx-auto max-w-7xl">
        <GalleryGrid
          images={displayedImages}
          onImageClick={handleImageClick}
          onFavorite={handleFavorite}
          favorites={favorites}
        />

        {/* Load More Button */}
        {visibleImages < filteredImages.length && (
          <div className="px-4 pb-12 text-center">
            <button
              type="button"
              onClick={handleLoadMore}
              className="rounded-lg bg-primary px-8 py-3 text-sm font-medium text-primary-foreground shadow-md transition-smooth hover-lift active-press focus-ring"
            >
              Load More Images
            </button>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      <LightboxModal
        image={selectedImage}
        onClose={handleCloseModal}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onShare={handleShare}
      />
    </div>
  );
};

export default GalleryInteractive;
